'use client'

import type { MouseEvent, ReactNode } from 'react'

import { browserNavigate } from '@/lib/browser-navigation'

interface CrawlSafeLinkProps {
    /**
     * The fully-resolved destination, locale prefix and all. Resolved by the caller
     * because navigation here is a full page load, often to a different app —
     * nothing downstream gets a chance to add the prefix an i18n `Link` would.
     */
    url: string
    /** Matches a former `target="_blank"` link. */
    newTab?: boolean
    /** Fired before navigation, so an analytics SDK has a chance to flush. */
    onSelect?: () => void
    className?: string
    /** Rendered as `data-testid`, for a caller whose own tests already select on one. */
    testId?: string
    children: ReactNode
}

/**
 * A link-like control for a destination that **must not appear in the rendered HTML** —
 * it carries the URL in JS rather than an `href`, so a crawler never sees it.
 * `rel="nofollow"` is not sufficient: that is a ranking hint, not a fetch ban. The
 * transient search routes are the archetype (`invariants.md#no-transient-search-links`),
 * but nothing here is search-specific — any never-index destination belongs on it.
 *
 * A `<button>`, not an hrefless `<a>`: that has a generic role, so it would need
 * `tabIndex` + `role` + key handlers to reimplement `<button>`, and would recover none of
 * the link affordances, which come from the `href` rather than the tag. What is given up
 * is the context menu and the status-bar URL preview — the paths by which the URL would
 * escape. Full rationale: `plans/repo/2026-08-10-no-transient-search-links.md`.
 */
export function CrawlSafeLink({
    url,
    newTab,
    onSelect,
    className,
    testId,
    children,
}: CrawlSafeLinkProps) {
    function handleClick() {
        // Fire analytics before navigation so the SDK has a chance to flush.
        onSelect?.()
        browserNavigate(url, { newTab })
    }

    // The middle button fires `auxclick`, not `click`, so without this it does nothing at
    // all. Right-click fires it too, hence the button check.
    function handleAuxClick(event: MouseEvent<HTMLButtonElement>) {
        if (event.button !== 1) {
            return
        }
        event.preventDefault()
        onSelect?.()
        browserNavigate(url, { newTab: true })
    }

    // `data-crawl-safe-link` serves one assertion: enumerating every control of this class
    // on a page. Role selection can name an individual control but not the class.
    return (
        <button
            type="button"
            data-crawl-safe-link=""
            data-testid={testId}
            onClick={handleClick}
            onAuxClick={handleAuxClick}
            className={className}
        >
            {children}
        </button>
    )
}
